import React, { useState } from 'react';
import { ChevronDown, X, SlidersHorizontal } from 'lucide-react';
import {
  COUNTRIES, CERTIFICATIONS, SUPPLIER_TYPES,
  SHIPPING_CAPABILITIES, PRODUCT_CATEGORIES, SUSTAINABILITY_CERTS,
} from '../data/mockData';

const Section = ({ title, defaultOpen = true, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-t border-hairline py-4">
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between label-overline"
        data-testid={`filter-section-${title.toLowerCase().replace(/\s+/g, '-')}`}
      >
        {title}
        <ChevronDown size={14} strokeWidth={1.5} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="mt-3 space-y-2">{children}</div>}
    </div>
  );
};

const CheckboxRow = ({ label, checked, onChange, count, testId }) => (
  <label className="flex cursor-pointer items-center gap-2.5 text-sm hover:text-black" data-testid={testId}>
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className="giwahs-filter-checkbox h-4 w-4 cursor-pointer appearance-none rounded-sm border"
      style={{
        borderColor: 'rgba(200,169,107,0.55)',
        backgroundImage: checked ? "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill='none' stroke='white' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round' d='M3 8l3 3 7-7'/%3E%3C/svg%3E\")" : 'none',
        backgroundSize: 'contain',
        backgroundRepeat: 'no-repeat',
      }}
    />
    <span className="flex-1 text-platinum-2">{label}</span>
    {count !== undefined && <span className="font-mono-data text-[10px] text-muted-warm">{count}</span>}
  </label>
);

export const buildEmptyFilters = () => ({
  countries: [], certifications: [], supplierTypes: [],
  shipping: [], categories: [], sustainability: [],
  moqMax: 50000, trustMin: 0, manufacturersOnly: false,
});

const FilterSidebar = ({ filters, setFilters, variant = 'supplier' }) => {
  const toggleArr = (key, val) => {
    setFilters(prev => ({
      ...prev,
      [key]: prev[key].includes(val) ? prev[key].filter(x => x !== val) : [...prev[key], val],
    }));
  };

  const reset = () => setFilters(buildEmptyFilters());

  return (
    <aside data-testid="filter-sidebar" className="rounded-2xl border border-hairline bg-[#0B0B0B] p-5 text-platinum">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <SlidersHorizontal size={14} strokeWidth={1.5} />
          <span className="label-overline">Filters</span>
        </div>
        <button onClick={reset} data-testid="filter-reset" className="label-overline text-platinum-2 hover:text-gold">Reset</button>
      </div>

      {variant === 'supplier' && (
        <label className="giwahs-pill-field mt-4 flex cursor-pointer items-center justify-between px-4 py-2.5">
          <span className="label-overline">Manufacturers only</span>
          <input
            data-testid="filter-manufacturers-only"
            type="checkbox"
            checked={filters.manufacturersOnly}
            onChange={(e) => setFilters({ ...filters, manufacturersOnly: e.target.checked })}
            className="giwahs-filter-checkbox h-4 w-4 appearance-none rounded-sm border"
            style={{
              borderColor: 'rgba(200,169,107,0.55)',
              backgroundImage: filters.manufacturersOnly ? "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill='none' stroke='white' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round' d='M3 8l3 3 7-7'/%3E%3C/svg%3E\")" : 'none',
              backgroundSize: 'contain',
              backgroundRepeat: 'no-repeat',
            }}
          />
        </label>
      )}

      <Section title="Product Category">
        {PRODUCT_CATEGORIES.map(c => (
          <CheckboxRow
            key={c.slug}
            label={c.name}
            count={c.count}
            checked={filters.categories.includes(c.slug)}
            onChange={() => toggleArr('categories', c.slug)}
            testId={`filter-cat-${c.slug}`}
          />
        ))}
      </Section>

      <Section title="Country / Region">
        {COUNTRIES.map(c => (
          <CheckboxRow
            key={c}
            label={c}
            checked={filters.countries.includes(c)}
            onChange={() => toggleArr('countries', c)}
            testId={`filter-country-${c.toLowerCase()}`}
          />
        ))}
      </Section>

      {variant === 'supplier' && (
        <Section title="Supplier Type">
          {SUPPLIER_TYPES.map(s => (
            <CheckboxRow
              key={s}
              label={s}
              checked={filters.supplierTypes.includes(s)}
              onChange={() => toggleArr('supplierTypes', s)}
              testId={`filter-type-${s.toLowerCase().replace(/\s+/g, '-')}`}
            />
          ))}
        </Section>
      )}

      <Section title="MOQ (max)">
        <div className="space-y-2">
          <input
            data-testid="filter-moq-slider"
            type="range" min={100} max={50000} step={100}
            value={filters.moqMax}
            onChange={(e) => setFilters({ ...filters, moqMax: Number(e.target.value) })}
            className="w-full accent-black"
          />
          <div className="flex justify-between font-mono-data text-xs">
            <span>100</span>
            <span className="text-black">≤ {filters.moqMax.toLocaleString()}</span>
            <span>50,000</span>
          </div>
        </div>
      </Section>

      {variant === 'supplier' && (
        <Section title="Trust Score (min)">
          <div className="space-y-2">
            <input
              data-testid="filter-trust-slider"
              type="range" min={0} max={100} step={5}
              value={filters.trustMin}
              onChange={(e) => setFilters({ ...filters, trustMin: Number(e.target.value) })}
              className="w-full accent-black"
            />
            <div className="flex justify-between font-mono-data text-xs">
              <span>0</span>
              <span className="text-black">≥ {filters.trustMin}/100</span>
              <span>100</span>
            </div>
          </div>
        </Section>
      )}

      <Section title="Certifications" defaultOpen={false}>
        {CERTIFICATIONS.map(c => (
          <CheckboxRow
            key={c}
            label={c}
            checked={filters.certifications.includes(c)}
            onChange={() => toggleArr('certifications', c)}
            testId={`filter-cert-${c.toLowerCase().replace(/\s+/g, '-')}`}
          />
        ))}
      </Section>

      <Section title="Sustainability" defaultOpen={false}>
        {SUSTAINABILITY_CERTS.map(c => (
          <CheckboxRow
            key={c}
            label={c}
            checked={filters.sustainability.includes(c)}
            onChange={() => toggleArr('sustainability', c)}
            testId={`filter-sust-${c.toLowerCase().replace(/\s+/g, '-')}`}
          />
        ))}
      </Section>

      <Section title="Shipping" defaultOpen={false}>
        {SHIPPING_CAPABILITIES.map(s => (
          <CheckboxRow
            key={s}
            label={s}
            checked={filters.shipping.includes(s)}
            onChange={() => toggleArr('shipping', s)}
            testId={`filter-ship-${s.toLowerCase().replace(/\s+/g, '-').replace('/', '-')}`}
          />
        ))}
      </Section>
    </aside>
  );
};

// Mobile drawer wrapper
export const MobileFilterDrawer = ({ open, onClose, children }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] lg:hidden" data-testid="mobile-filter-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute bottom-0 left-0 right-0 max-h-[88vh] overflow-y-auto border-t border-black bg-white">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-black bg-white px-5 py-3">
          <span className="label-overline">Filters</span>
          <button onClick={onClose} className="flex h-9 w-9 items-center justify-center border border-black" data-testid="mobile-filter-close">
            <X size={16} strokeWidth={1.5} />
          </button>
        </div>
        <div className="p-4">{children}</div>
        <div className="sticky bottom-0 border-t border-black bg-white p-3">
          <button onClick={onClose} className="btn-giwahs btn-giwahs-primary w-full" data-testid="mobile-filter-apply">Show results</button>
        </div>
      </div>
    </div>
  );
};

export default FilterSidebar;
